import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";

const Logo = ({ size = "medium", className = "", animated = true }) => {
  const controls = useAnimation();

  const sizes = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16",
    xlarge: "w-24 h-24",
  };

  const logoPath = "/images/logo.png";

  // Animation d'entrée automatique
  useEffect(() => {
    if (animated) {
      controls.start({
        scale: [0.8, 1.1, 1],
        rotate: [0, 10, 0],
        opacity: [0, 1],
        transition: {
          duration: 1.5,
          ease: "easeOut",
        },
      });
    }
  }, [animated, controls]);

  return (
    <motion.div
      className={`relative ${sizes[size]} ${className} group`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      initial={animated ? { scale: 0.8, opacity: 0 } : false}
      animate={controls}
    >
      {/* Effet de lueur d'arrière-plan amélioré */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-green-500/30 blur-xl group-hover:blur-2xl group-hover:opacity-80 transition-all duration-500" />

      {/* Fond avec effet de profondeur */}
      <div className="absolute inset-1 rounded-lg bg-slate-800/40 backdrop-blur-sm" />

      {/* Étoiles en arrière-plan avec containment amélioré */}
      <div className="absolute inset-0 overflow-hidden rounded-xl">
        <div className="stars-1 opacity-60"></div>
        <div className="stars-2 opacity-50"></div>
        <div className="stars-3 opacity-40"></div>
      </div>

      {/* Conteneur du logo avec effets */}
      <div className="relative w-full h-full">
        {/* Bordure animée avec coins arrondis */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 animate-spin-slow opacity-70" />

        {/* Effet de halo intérieur */}
        <div className="absolute inset-[1px] rounded-xl bg-gradient-to-r from-blue-400/10 to-teal-400/10" />

        {/* Pulsation */}
        <motion.div
          className="absolute inset-0 rounded-xl bg-white/5"
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        />

        {/* Conteneur de l'image avec coins arrondis */}
        <div className="absolute inset-[2px] rounded-lg bg-gradient-to-b from-slate-700 to-slate-900 overflow-hidden flex items-center justify-center backdrop-blur-sm">
          {/* Image avec masque et ombres */}
          <div className="relative w-[85%] h-[85%] rounded-lg overflow-hidden flex items-center justify-center">
            <motion.img
              src={logoPath}
              alt="Baobab Exchange Logo"
              className="w-[90%] h-[90%] object-contain transition-transform duration-300 group-hover:scale-110 drop-shadow-lg"
              style={{
                filter: "drop-shadow(0 0 8px rgba(0, 255, 170, 0.3))",
                borderRadius: "0.5rem",
              }}
              whileHover={{
                rotateZ: [0, 5, -5, 0],
                transition: { duration: 0.5 },
              }}
            />
            {/* Reflet lumineux sur l'image */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        </div>

        {/* Effet de brillance au survol amélioré */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -rotate-45" />

        {/* Points lumineux aux coins */}
        <div className="absolute top-0 left-0 w-1 h-1 rounded-full bg-blue-400 opacity-70 group-hover:opacity-100 group-hover:w-1.5 group-hover:h-1.5 transition-all duration-300"></div>
        <div className="absolute top-0 right-0 w-1 h-1 rounded-full bg-purple-400 opacity-70 group-hover:opacity-100 group-hover:w-1.5 group-hover:h-1.5 transition-all duration-300"></div>
        <div className="absolute bottom-0 left-0 w-1 h-1 rounded-full bg-teal-400 opacity-70 group-hover:opacity-100 group-hover:w-1.5 group-hover:h-1.5 transition-all duration-300"></div>
        <div className="absolute bottom-0 right-0 w-1 h-1 rounded-full bg-green-400 opacity-70 group-hover:opacity-100 group-hover:w-1.5 group-hover:h-1.5 transition-all duration-300"></div>
      </div>
    </motion.div>
  );
};

export default Logo;
